import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  DdDetailsIcon,
  DdAssigneeIcon,
  DdRecurrenceIcon,
  DdClockIcon,
  PaperclipIcon,
  TrashIcon,
  TemplateDocIcon,
  ImagePlaceholderIcon,
} from '../../assets/icons';

const FIELD =
  'w-full rounded-lg border border-transparent bg-[#f9fafb] px-4 text-sm text-ink placeholder:text-ink-secondary transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const ASSIGNEES = ['Ravi Kumar', 'Anita Sharma', 'Mahesh Patil', 'Elena Rodriguez'];
const PRIORITIES: { key: string; lvl: string; label: string; critical?: boolean }[] = [
  { key: 'low', lvl: 'Level 01', label: 'Low' },
  { key: 'medium', lvl: 'Level 02', label: 'Medium' },
  { key: 'high', lvl: 'Level 03', label: 'High' },
  { key: 'critical', lvl: 'Level 04', label: 'Critical', critical: true },
];

interface DDFormValues {
  title: string;
  oneLine: string;
  description: string;
  assignee: string;
  priority: string;
  dueDate: string;
  dueTime: string;
  duration: string;
  repeat: boolean;
  frequency: string;
  startDate: string;
  endDate: string;
}

const validationSchema = Yup.object({
  title: Yup.string().required('Strategic title is required'),
  oneLine: Yup.string().required('One-line direction is required'),
});

function Card({
  Icon,
  iconBg = 'bg-brand-shade text-brand',
  title,
  action,
  children,
}: {
  Icon: ReactNode;
  iconBg?: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-[#f3f4f6] bg-white p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={['flex size-8 shrink-0 items-center justify-center rounded-lg', iconBg].join(' ')}>
            {Icon}
          </span>
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-xs font-medium text-ink">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

export default function CreateDailyDirectionPage() {
  const navigate = useNavigate();

  const formik = useFormik<DDFormValues>({
    initialValues: {
      title: '',
      oneLine: '',
      description: '',
      assignee: '',
      priority: 'high',
      dueDate: '',
      dueTime: '',
      duration: '',
      repeat: false,
      frequency: 'daily',
      startDate: '',
      endDate: '',
    },
    validationSchema,
    onSubmit: (values) => {
      // TODO: wire to a createDirection mutation once the API exists.
      // eslint-disable-next-line no-console
      console.log('Create daily direction', values);
      navigate('/user/directions');
    },
  });

  const { values, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <Breadcrumbs
          items={[
            { label: 'Daily Directions', to: '/user/directions' },
            { label: 'Create Daily Direction' },
          ]}
        />
        <h1 className="text-2xl font-semibold text-ink">Create Daily Direction</h1>
        <p className="max-w-[576px] text-sm text-ink-secondary">
          Initialize a new operational directive. Define the scope, priority, and temporal
          constraints for tactical execution.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Direction Details */}
          <Card Icon={<DdDetailsIcon className="size-4" />} title="Direction Details">
            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-2">
                <Label required>Strategic Title</Label>
                <input
                  name="title"
                  placeholder="e.g., Q4 Logistics Re-alignment"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
              </label>
              <label className="flex flex-col gap-2">
                <Label required>One-line Direction</Label>
                <input
                  name="oneLine"
                  placeholder="Summarize the core objective in under 100 characters"
                  value={values.oneLine}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={FIELD}
                />
              </label>
              <label className="flex flex-col gap-2">
                <Label>Full Operational Description</Label>
                <textarea
                  name="description"
                  rows={4}
                  placeholder="Provide detailed context, secondary objectives, and mission-critical notes..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={[FIELD, 'resize-none py-3'].join(' ')}
                />
              </label>
            </div>
          </Card>

          {/* Assignee */}
          <Card
            Icon={<DdAssigneeIcon className="h-3.5 w-[18px]" />}
            title="Assignee"
            action={
              <button type="button" className="text-sm font-medium text-brand hover:underline">
                + Add More Assignee
              </button>
            }
          >
            <div className="flex flex-col gap-2">
              <Label>Select User</Label>
              <div className="flex items-center gap-3">
                <Select
                  className="field-muted flex-1"
                  placeholder="Choose an assignee..."
                  value={values.assignee || undefined}
                  onChange={(v) => setFieldValue('assignee', v)}
                  options={ASSIGNEES.map((a) => ({ value: a, label: a }))}
                />
                <button
                  type="button"
                  aria-label="Remove assignee"
                  className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#fef2f2] text-[#ef4444] transition-colors hover:bg-[#fee2e2]"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            </div>
          </Card>

          {/* Recurrence */}
          <Card
            Icon={<DdRecurrenceIcon className="size-3.5" />}
            title="Recurrence"
            action={
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-ink">Repeat this Direction</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={values.repeat}
                  onClick={() => setFieldValue('repeat', !values.repeat)}
                  className={['relative h-5 w-11 shrink-0 rounded-full transition-colors', values.repeat ? 'bg-brand' : 'bg-[#d1d5db]'].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-0 size-5 rounded-full border-4 bg-white transition-all',
                      values.repeat ? 'left-6 border-brand' : 'left-0 border-[#46556f]',
                    ].join(' ')}
                  />
                </button>
              </div>
            }
          >
            <div
              className={[
                'flex flex-col gap-4 sm:flex-row',
                values.repeat ? '' : 'pointer-events-none opacity-50',
              ].join(' ')}
            >
              <div className="flex flex-1 flex-col gap-2">
                <Label>Frequency</Label>
                <Select
                  className="field-muted w-full"
                  value={values.frequency}
                  onChange={(v) => setFieldValue('frequency', v)}
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Label>Start Date</Label>
                <DatePicker
                  className="field-muted w-full"
                  format="MM/DD/YYYY"
                  placeholder="mm/dd/yyyy"
                  value={values.startDate ? dayjs(values.startDate) : null}
                  onChange={(d) => setFieldValue('startDate', d ? d.format('YYYY-MM-DD') : '')}
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Label>End Date</Label>
                <DatePicker
                  className="field-muted w-full"
                  format="MM/DD/YYYY"
                  placeholder="mm/dd/yyyy"
                  value={values.endDate ? dayjs(values.endDate) : null}
                  onChange={(d) => setFieldValue('endDate', d ? d.format('YYYY-MM-DD') : '')}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Priority */}
          <Card
            Icon={<span className="font-black text-ink">!</span>}
            iconBg="bg-[#f3f4f6] text-ink"
            title="Priority"
          >
            <div className="grid grid-cols-2 gap-3">
              {PRIORITIES.map((p) => {
                const selected = values.priority === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setFieldValue('priority', p.key)}
                    className={[
                      'flex flex-col gap-1 rounded-lg p-3 text-left transition-colors',
                      selected ? 'border-2 border-brand bg-brand-shade' : 'border border-transparent bg-[#f9fafb] hover:border-brand/30',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'text-[10px] font-semibold uppercase tracking-[0.25px]',
                        selected ? 'text-brand' : p.critical ? 'text-[#ef4444]' : 'text-ink-secondary',
                      ].join(' ')}
                    >
                      {p.lvl}
                    </span>
                    <span className="text-sm font-medium text-ink">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Schedule */}
          <Card Icon={<DdClockIcon className="size-3.5" />} title="Schedule">
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <Label>Due Date</Label>
                <DatePicker
                  className="field-muted w-full"
                  format="MM/DD/YYYY"
                  placeholder="mm/dd/yyyy"
                  value={values.dueDate ? dayjs(values.dueDate) : null}
                  onChange={(d) => setFieldValue('dueDate', d ? d.format('YYYY-MM-DD') : '')}
                />
              </label>
              <div className="flex gap-4">
                <label className="flex flex-1 flex-col gap-2">
                  <Label>Due Time</Label>
                  <input
                    name="dueTime"
                    placeholder="05:00 PM"
                    value={values.dueTime}
                    onChange={handleChange}
                    className={FIELD}
                  />
                </label>
                <label className="flex flex-1 flex-col gap-2">
                  <Label>Duration</Label>
                  <input
                    name="duration"
                    placeholder="e.g. 2 Hours"
                    value={values.duration}
                    onChange={handleChange}
                    className={FIELD}
                  />
                </label>
              </div>
            </div>
          </Card>

          {/* Assets */}
          <Card Icon={<PaperclipIcon className="h-3.5 w-3 text-brand" />} title="Assets">
            <div className="flex gap-4">
              <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                <TemplateDocIcon className="h-6 w-[18px] text-ink-secondary" />
                <span className="text-xs font-medium text-ink">Upload Documents</span>
                <span className="text-[10px] text-ink-secondary">PDF, DOC up to 10MB</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" />
              </label>
              <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] p-6 text-center transition-colors hover:border-brand/50">
                <ImagePlaceholderIcon className="size-6 text-ink-secondary" />
                <span className="text-xs font-medium text-ink">Upload Photos</span>
                <span className="text-[10px] text-ink-secondary">JPG, PNG captures</span>
                <input type="file" accept=".jpg,.jpeg,.png" className="hidden" />
              </label>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-4 border-t border-[#e5e7eb] pt-6">
        <button
          type="button"
          onClick={() => navigate('/user/directions')}
          className="rounded-lg px-6 py-2.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade"
        >
          Save Draft
        </button>
        <button
          type="submit"
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
        >
          Submit
        </button>
      </div>
    </form>
  );
}
